'use client';

import { motion, Variants } from 'framer-motion';
import { Camera, Cloud, BookOpen } from 'lucide-react';

const steps = [
  {
    icon: Camera,
    title: 'Camera',
    description: 'Capture your child\'s artwork instead of boxing it away.',
  },
  {
    icon: Cloud,
    title: 'Cloud',
    description: 'Store it free forever in your personal gallery.',
  },
  {
    icon: BookOpen,
    title: 'Stories',
    description: 'Generate magical stories from their artwork.',
  },
];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

/**
 * How It Works section - 3-step process with large icons
 *
 * Features:
 * - Large Camera, Cloud, Book icons
 * - Clean cream background
 * - Simple step descriptions
 */
export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-16 md:py-20 bg-cream-100">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-charcoal-600">
            How it Works
          </h2>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-3 gap-6 md:gap-12"
        >
          {steps.map((step) => (
            <motion.div
              key={step.title}
              variants={itemVariants}
              className="text-center"
            >
              {/* Large Icon */}
              <div className="flex justify-center mb-4 md:mb-6">
                <step.icon
                  className="w-20 h-20 md:w-28 md:h-28 text-taupe-400"
                  strokeWidth={0.75}
                />
              </div>

              {/* Title */}
              <h3 className="text-sm md:text-base font-semibold text-charcoal-600 mb-2">
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-xs md:text-sm text-taupe-500 leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
